import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Divider,
    Alert,
    Snackbar,
    CircularProgress,
    Stack,
} from '@mui/material';
import { Business as BusinessIcon, Save as SaveIcon } from '@mui/icons-material';
import useFetch from '../../../../hooks/useFetch';
import api from '../../../../config/axiosConfig';
import RTL from '../../../../utils/RTL';
import WorkHoursManagement from '../WorkHoursManagement/WorkHoursManagement';

const API_ENDPOINT = '/institutes/api/v1/teacher-working-days/institute-info/';

function InstituteInfo() {
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [isSaving, setIsSaving] = useState(false);

    const { data, loading, error } = useFetch(API_ENDPOINT);

    const [form, setForm] = useState({ name: '', telephone: '', address: '' });

    React.useEffect(() => {
        if (data) {
            setForm({
                name: data.name || '',
                telephone: data.telephone || '',
                address: data.address || '',
            });
        }
    }, [data]);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        try {
            await api.patch(API_ENDPOINT, {
                telephone: form.telephone,
                address: form.address,
            });
            setSnackbar({ open: true, message: 'اطلاعات مؤسسه با موفقیت ذخیره شد.', severity: 'success' });
        } catch (err) {
            const message = err?.response?.data?.detail
                || err?.response?.data?.error
                || 'خطا در ذخیرهٔ اطلاعات';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            setIsSaving(false);
        }
    }, [form]);

    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">{error || 'خطا در بارگذاری اطلاعات مؤسسه'}</Alert>
            </Box>
        );
    }

    return (
        <RTL>
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                    اطلاعات مؤسسه
                </Typography>

                <Paper elevation={2} sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <BusinessIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            مشخصات تماس
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        تلفن و نشانی واردشده در پابرگ گزارش‌ها و ایمیل‌های مؤسسه نمایش داده می‌شود.
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    <Stack spacing={3}>
                        <TextField
                            label="نام مؤسسه"
                            value={form.name}
                            fullWidth
                            disabled
                            helperText="نام مؤسسه از این بخش قابل تغییر نیست."
                        />
                        <TextField
                            label="تلفن"
                            value={form.telephone}
                            onChange={(e) => handleChange('telephone', e.target.value)}
                            fullWidth
                            placeholder="مثال: ۰۲۱-۱۲۳۴۵۶۷۸"
                        />
                        <TextField
                            label="نشانی"
                            value={form.address}
                            onChange={(e) => handleChange('address', e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="نشانی کامل مؤسسه"
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? 'در حال ذخیره...' : 'ذخیره'}
                            </Button>
                        </Box>
                    </Stack>
                </Paper>

                <Box sx={{ mt: 3 }}>
                    <WorkHoursManagement />
                </Box>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </RTL>
    );
}

export default InstituteInfo;
